import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { General } from 'icons/untitledui';
import {
  SecondaryDarkIcon,
  SecondaryLightIcon,
} from 'theme/components/Buttons.style';
import useClickOutside from 'shared/hooks/useClickOutside';
import { usePosthogCapture } from 'events/posthogCapture/_index';
import * as Styled from '../../styles/FilterGroupingControl.style';
import FilterMenu from './FilterMenu';
import GroupingMenu from './GroupingMenu';
import UrgencyFilterMenu from './UrgencyFilterMenu';
import DepartmentFilterMenu from './DepartmentFilterMenu';
import PeopleFilterMenu from './PeopleFilterMenu';
import TagFilterMenu from './TagFilterMenu';
import VisibilityFilterMenu from './VisibilityFilterMenu';
import SourceFilterMenu from './SourceFilterMenu';
import SignalFilterMenu from './SignalFilterMenu';
import {
  EMPTY_FILTERS,
  FilterKey,
  FilterOptions,
  FilterValues,
  GroupingKey,
  Source,
  Urgency,
  Visibility,
} from './_types';

type OpenPanel = 'none' | 'filter' | 'grouping';

function getUsableViewportBounds(...excludedRoots: Array<HTMLElement | null>): {
  usableTop: number;
  usableBottom: number;
} {
  const viewportH = window.innerHeight;
  const viewportW = window.innerWidth;
  let usableTop = 0;
  let usableBottom = viewportH;

  document.querySelectorAll<HTMLElement>('*').forEach((el) => {
    if (excludedRoots.some((root) => root && root.contains(el))) return;
    const cs = window.getComputedStyle(el);
    if (cs.position !== 'fixed' && cs.position !== 'sticky') return;
    if (cs.visibility === 'hidden' || cs.display === 'none') return;
    if (parseFloat(cs.opacity) === 0) return;
    const rect = el.getBoundingClientRect();
    if (rect.width < viewportW * 0.25 || rect.height <= 0) return;
    const midY = rect.top + rect.height / 2;
    if (midY < viewportH * 0.5) {
      usableTop = Math.max(usableTop, rect.bottom);
    } else {
      usableBottom = Math.min(usableBottom, rect.top);
    }
  });

  return { usableTop, usableBottom };
}

interface FilterGroupingControlProps {
  values: FilterValues;
  grouping: GroupingKey;
  options: FilterOptions;
  onValuesChange: (values: FilterValues) => void;
  onGroupingChange: (grouping: GroupingKey) => void;
  initialOpenPanel?: OpenPanel;
  initialActiveFilter?: FilterKey | null;
}

export default function FilterGroupingControl({
  values,
  grouping,
  options,
  onValuesChange,
  onGroupingChange,
  initialOpenPanel = 'none',
  initialActiveFilter = null,
}: FilterGroupingControlProps): JSX.Element {
  const { t } = useTranslation('pageEvents');
  const capture = usePosthogCapture();
  const rootRef = useRef<HTMLDivElement>(null);
  const portalRef = useRef<HTMLDivElement>(null);
  const submenuRef = useRef<HTMLDivElement>(null);
  const [openPanel, setOpenPanel] = useState<OpenPanel>(initialOpenPanel);
  const [activeFilter, setActiveFilter] = useState<FilterKey | null>(
    initialActiveFilter
  );
  const [submenuTopOffset, setSubmenuTopOffset] = useState(0);
  const [anchorPos, setAnchorPos] = useState<{
    top: number;
    left: number;
    mainShift: number;
    submenuShift: number;
  } | null>(null);

  const closeAll = useCallback(() => {
    setOpenPanel('none');
    setActiveFilter(null);
    setSubmenuTopOffset(0);
  }, []);

  useClickOutside([rootRef, portalRef], closeAll, openPanel !== 'none');

  useLayoutEffect(() => {
    if (openPanel === 'none') {
      setAnchorPos(null);
      return undefined;
    }
    const update = () => {
      const node = rootRef.current;
      if (!node) return;
      const rect = node.getBoundingClientRect();
      const popoverWidth = 280;
      const popoverFullHeight = 444;
      const popoverGap = 10;
      const viewportMargin = 10;
      const submenuLeftShift = 40;
      const minLeft = viewportMargin + submenuLeftShift;
      const maxLeft = window.innerWidth - popoverWidth - viewportMargin;
      const desiredLeft = rect.right - popoverWidth;
      const left = Math.max(minLeft, Math.min(desiredLeft, maxLeft));

      const { usableBottom } = getUsableViewportBounds(
        rootRef.current,
        portalRef.current
      );
      const maxAllowedBottom = usableBottom - viewportMargin;

      const naturalMainBottom = rect.bottom + popoverGap + popoverFullHeight;
      const mainShift = Math.max(0, naturalMainBottom - maxAllowedBottom);

      const submenuCard = submenuRef.current?.firstElementChild;
      const submenuHeight =
        submenuCard instanceof HTMLElement
          ? submenuCard.offsetHeight
          : popoverFullHeight;
      const naturalSubmenuBottom =
        rect.bottom + popoverGap + submenuTopOffset + submenuHeight;
      const submenuShift =
        activeFilter !== null
          ? Math.max(0, naturalSubmenuBottom - maxAllowedBottom)
          : 0;

      setAnchorPos({
        top: rect.bottom,
        left,
        mainShift,
        submenuShift,
      });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [openPanel, activeFilter, submenuTopOffset]);

  const handleFilterClick = () => {
    if (openPanel === 'filter') {
      closeAll();
    } else {
      setOpenPanel('filter');
      setActiveFilter(null);
      capture.filterOpened();
    }
  };

  const handleGroupingClick = () => {
    if (openPanel === 'grouping') {
      closeAll();
    } else {
      setOpenPanel('grouping');
      setActiveFilter(null);
      capture.groupingOpened();
    }
  };

  const handleSelectCategory = (category: FilterKey, anchorTop: number) => {
    if (activeFilter !== null) {
      setActiveFilter(null);
      return;
    }
    setSubmenuTopOffset(anchorTop);
    setActiveFilter(category);
    capture.filterCategoryOpened({ category });
  };

  const handlePrimaryClick = () => {
    if (activeFilter !== null) {
      setActiveFilter(null);
    }
  };

  const handleReset = () => {
    onValuesChange(EMPTY_FILTERS);
    capture.filterReset({ from: 'menu' });
  };

  const captureValueChange = (
    category: FilterKey,
    value: string | null
  ): void => {
    if (value === null) {
      capture.filterValueCleared({ category });
    } else {
      capture.filterValueSelected({ category, value });
    }
  };

  const setUrgency = (value: Urgency | null) => {
    onValuesChange({ ...values, urgency: value });
    captureValueChange('urgency', value);
  };

  const setDepartment = (id: string | null) => {
    onValuesChange({ ...values, department: id });
    captureValueChange('department', id);
  };

  const setPerson = (id: string | null) => {
    onValuesChange({ ...values, person: id });
    captureValueChange('people', id);
  };

  const setTag = (id: string | null) => {
    onValuesChange({ ...values, tag: id });
    captureValueChange('tags', id);
  };

  const setVisibility = (value: Visibility | null) => {
    onValuesChange({ ...values, visibility: value });
    captureValueChange('visibility', value);
  };

  const setSource = (value: Source | null) => {
    const keepsSignal = value !== 'user';
    onValuesChange({
      ...values,
      source: value,
      signal: keepsSignal ? values.signal : null,
    });
    captureValueChange('source', value);
  };

  const setSignal = (id: string | null) => {
    onValuesChange({
      ...values,
      signal: id,
      source: id ? 'camera' : values.source,
    });
    captureValueChange('signal', id);
  };

  const handleGroupingChange = (next: GroupingKey) => {
    onGroupingChange(next);
    capture.groupingChanged({ grouping: next });
  };

  const hasActiveFilters = Object.values(values).some(
    (value) => value !== null
  );

  return (
    <Styled.Root ref={rootRef}>
      <Styled.TriggerPill>
        {openPanel === 'filter' || hasActiveFilters ? (
          <SecondaryDarkIcon
            type="button"
            $size="small"
            aria-label={t('filterGrouping.filter.triggerAriaLabel')}
            aria-expanded={openPanel === 'filter'}
            aria-haspopup="menu"
            onClick={handleFilterClick}
          >
            <General.FilterLines />
          </SecondaryDarkIcon>
        ) : (
          <SecondaryLightIcon
            type="button"
            $size="small"
            aria-label={t('filterGrouping.filter.triggerAriaLabel')}
            aria-expanded={false}
            aria-haspopup="menu"
            onClick={handleFilterClick}
          >
            <General.FilterLines />
          </SecondaryLightIcon>
        )}
        {openPanel === 'grouping' || grouping !== 'default' ? (
          <SecondaryDarkIcon
            type="button"
            $size="small"
            aria-label={t('filterGrouping.grouping.triggerAriaLabel')}
            aria-expanded={openPanel === 'grouping'}
            aria-haspopup="menu"
            onClick={handleGroupingClick}
          >
            <General.Rows01 />
          </SecondaryDarkIcon>
        ) : (
          <SecondaryLightIcon
            type="button"
            $size="small"
            aria-label={t('filterGrouping.grouping.triggerAriaLabel')}
            aria-expanded={false}
            aria-haspopup="menu"
            onClick={handleGroupingClick}
          >
            <General.Rows01 />
          </SecondaryLightIcon>
        )}
      </Styled.TriggerPill>

      {openPanel !== 'none' &&
        anchorPos &&
        createPortal(
          <Styled.PortalAnchor
            ref={portalRef}
            style={{ top: anchorPos.top, left: anchorPos.left }}
            $mainShift={anchorPos.mainShift}
            $submenuShift={anchorPos.submenuShift}
          >
            {openPanel === 'filter' && (
              <FilterMenu
                values={values}
                options={options}
                onSelectCategory={handleSelectCategory}
                onReset={handleReset}
                onPrimaryClick={handlePrimaryClick}
              />
            )}

            <div ref={submenuRef}>
              {openPanel === 'filter' && activeFilter === 'urgency' && (
                <UrgencyFilterMenu
                  selected={values.urgency}
                  onSelect={setUrgency}
                  topOffset={submenuTopOffset}
                />
              )}

              {openPanel === 'filter' && activeFilter === 'department' && (
                <DepartmentFilterMenu
                  departments={options.departments}
                  selected={values.department}
                  onSelect={setDepartment}
                  topOffset={submenuTopOffset}
                />
              )}

              {openPanel === 'filter' && activeFilter === 'people' && (
                <PeopleFilterMenu
                  people={options.people}
                  selected={values.person}
                  onSelect={setPerson}
                  topOffset={submenuTopOffset}
                />
              )}

              {openPanel === 'filter' && activeFilter === 'tags' && (
                <TagFilterMenu
                  tags={options.tags}
                  selected={values.tag}
                  onSelect={setTag}
                  topOffset={submenuTopOffset}
                />
              )}

              {openPanel === 'filter' && activeFilter === 'visibility' && (
                <VisibilityFilterMenu
                  selected={values.visibility}
                  onSelect={setVisibility}
                  topOffset={submenuTopOffset}
                />
              )}

              {openPanel === 'filter' && activeFilter === 'source' && (
                <SourceFilterMenu
                  selected={values.source}
                  onSelect={setSource}
                  topOffset={submenuTopOffset}
                />
              )}

              {openPanel === 'filter' && activeFilter === 'signal' && (
                <SignalFilterMenu
                  signals={options.signals}
                  selected={values.signal}
                  onSelect={setSignal}
                  topOffset={submenuTopOffset}
                />
              )}
            </div>

            {openPanel === 'grouping' && (
              <GroupingMenu value={grouping} onChange={handleGroupingChange} />
            )}
          </Styled.PortalAnchor>,
          document.body
        )}
    </Styled.Root>
  );
}
